import { useState, useEffect, useCallback } from 'react';
import { pushAPI } from '../lib/api';

// Converte la chiave VAPID base64url in Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/**
 * Hook per gestire le Push Notification Web.
 * Restituisce:
 *   permission  — 'default' | 'granted' | 'denied'
 *   subscribed  — true se già iscritto
 *   requesting  — true durante la richiesta
 *   requestPermission() — chiede permesso e si iscrive
 *   unsubscribe()
 */
export const usePushNotifications = () => {
  const [permission, setPermission]   = useState(Notification.permission ?? 'default');
  const [subscribed, setSubscribed]   = useState(false);
  const [requesting, setRequesting]   = useState(false);

  // Controlla se già iscritto all'avvio
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const existing = await reg.pushManager.getSubscription();
      setSubscribed(!!existing);
    });
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      alert('Questo browser non supporta le Push Notification.');
      return;
    }
    setRequesting(true);
    try {
      // 1. Chiedi permesso
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') return;

      // 2. Ottieni service worker registration
      const reg = await navigator.serviceWorker.ready;

      // 3. Ottieni VAPID public key dal backend
      const { data } = await pushAPI.getVapidKey();
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);

      // 4. Crea subscription
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // 5. Invia subscription al backend
      const subJson = subscription.toJSON();
      await pushAPI.subscribe({
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setSubscribed(true);
    } catch (err) {
      console.error('[Push] Errore iscrizione:', err);
    } finally {
      setRequesting(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (!subscription) return;
      const subJson = subscription.toJSON();
      await pushAPI.unsubscribe({ endpoint: subJson.endpoint, keys: subJson.keys });
      await subscription.unsubscribe();
      setSubscribed(false);
    } catch (err) {
      console.error('[Push] Errore disiscrizione:', err);
    }
  }, []);

  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  return { permission, subscribed, requesting, supported, requestPermission, unsubscribe };
};
